import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/**
 * Per-workstation "chair" selection for active-patient DICOM routing.
 *
 * A clinician picks the operatory they're seated at once; the choice is kept in
 * localStorage so it survives refreshes on the same machine. When a patient is
 * opened, the X-ray viewer publishes `(operatory → patient)` to the acquisition
 * context so incoming images route to that patient. Cleared on logout
 * (localStorage is wiped) and resettable from the chair picker.
 */

const STORAGE_KEY = 'activeOperatoryId'

const readInitial = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

interface OperatorySessionState {
  selectedOperatoryId: string | null
}

const initialState: OperatorySessionState = {
  selectedOperatoryId: readInitial(),
}

const operatorySessionSlice = createSlice({
  name: 'operatorySession',
  initialState,
  reducers: {
    setActiveOperatory: (state, action: PayloadAction<string | null>) => {
      state.selectedOperatoryId = action.payload
      try {
        if (action.payload) {
          localStorage.setItem(STORAGE_KEY, action.payload)
        } else {
          localStorage.removeItem(STORAGE_KEY)
        }
      } catch {
        // localStorage may be unavailable (private mode); ignore.
      }
    },
  },
  extraReducers: (builder) => {
    // Reset on logout — auth.action clears localStorage, so mirror that here.
    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled',
      (state) => {
        state.selectedOperatoryId = null
      },
    )
  },
})

export const { setActiveOperatory } = operatorySessionSlice.actions
export default operatorySessionSlice.reducer
